import { FC, useEffect, useState } from "react";
import classes from './studentsWidget.module.scss'
import { IStudent, studentService } from "../../../../entities/student";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useMyActions } from "../../../../entities/my";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchItems } from "../../../../features/searchItems";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../app/store/store";
import { CommentsCount } from "../../../../entities/student/ui/item/CommentsCount";
import { FEATURES, hasFeature } from "../../../../entities/my";
import tbankLogo from "../../../../shared/lib/assets/tbank.png";
import alphaLogo from "../../../../shared/lib/assets/alpha.png";
import tochkaLogo from "../../../../shared/lib/assets/tochka.png";
import { SurfaceBlock } from "../../../../shared/ui/surfaceBlock";
import { LabelBadges } from '../../../../shared/ui/labels';

interface IProps {
    request: () => Promise<{students: IStudent[], students_count: number}>
    filterRequest?: (workTgIds: number[], isLost: boolean, paymentIds: number[], labels: string[], tagIds: number[]) => Promise<{students: IStudent[], students_count: number}>
    filtersEnabled?: boolean;
    highlight?: boolean;
    triggerReq?: boolean;
    forceServerFilter?: boolean;
}

export const StudentsWidget: FC<IProps> = ({request, filterRequest, filtersEnabled=true, highlight=false, triggerReq, forceServerFilter=false}) => {

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [students, setStudents] = useState<IStudent[]>([])
    const [studentsCount, setStudentsCount] = useState<number>(0)
    const [studentsSearch, setStudentsSearch] = useState<IStudent[]>([])

    const [params] = useSearchParams()
    const navigate = useNavigate()

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {my} = useAppSelector(s => s.myReducer)

    const getData = async (req: () => Promise<{students: IStudent[], students_count: number}>) => {
        try{
            setIsLoading(true)
            const studentsRes = await req()
            setStudentsCount(studentsRes.students_count)
            setStudents(studentsRes.students)
            setStudentsSearch(studentsRes.students)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении списка студентов', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onSelectedFilters = () => {
        if(!filtersEnabled){
            getData(request)
            return
        }

        const isLost = !!params.get('is_lost');
        const workTgIds = params.getAll('work_tg_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id))
        const paymentIds = params.getAll('payment_ids').map(id => parseInt(id, 10))
        const labels = params.getAll('labels')
        const tagIds = params.getAll('tag_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id))
        if(filterRequest || forceServerFilter){
            getData(() => filterRequest
                ? filterRequest(workTgIds, isLost, paymentIds, labels, tagIds)
                : studentService.getAllByFilters(workTgIds, isLost, undefined, paymentIds, labels, tagIds))
            return
        }
        getData(request)
    }

    useEffect(() => {
        onSelectedFilters()
    }, [params, triggerReq])

    const getPaymentLogo = (paymentName: string) => {
        const normalizedPaymentName = paymentName.trim().toLowerCase()

        if(
            normalizedPaymentName.includes('т-банк')
            || normalizedPaymentName.includes('тбанк')
            || normalizedPaymentName.includes('тиньк')
            || normalizedPaymentName.includes('tbank')
        ){
            return {src: tbankLogo, alt: 'Т-Банк'}
        }

        if(
            normalizedPaymentName.includes('альфа')
            || normalizedPaymentName.includes('alpha')
        ){
            return {src: alphaLogo, alt: 'Альфа'}
        }

        if(
            normalizedPaymentName.includes('точка')
            || normalizedPaymentName.includes('tochka')
        ){
            return {src: tochkaLogo, alt: 'Точка'}
        }

        return null
    }


    return (
        <section className={classes.container}>
            <section className={classes.search}>
                <section className={classes.searchItems}>
                    <SearchItems
                        placeholder="Введите фио студента или родителя"
                        items={students.map(
                            student => ({
                                ...student, 
                                name: student.last_name + ' ' + student.first_name + ' ' + student.middle_name + ' ' + student.parent_full_name
                            })
                        )}
                        setItems={setStudentsSearch}
                    />
                </section>
                {
                    !isLoading
                        &&
                    <section className={classes.count}>
                        Кол-во студентов: {studentsCount}
                    </section>
                }
            </section>
            {
                isLoading
                    ?
                <section className="loader"><LoaderSpinner /></section>
                    :
                <section className={classes.list}>
                    {studentsSearch.map(student => {
                        const targetUserId = student.user_id
                        const canViewStudentFull = hasFeature(my, FEATURES.STUDENTS.VIEW_FULL, targetUserId)
                        const canOpenStudentCard = hasFeature(my, FEATURES.STUDENTS.CARD_READ, targetUserId)
                        const canReadStudentComments = hasFeature(my, FEATURES.STUDENTS.CARD_COMMENTS_READ, targetUserId)
                        const studentName = `${student.last_name} ${student.first_name} ${student.middle_name}`.trim()
                        const parentFullName = student.parent_full_name?.trim()
                        const tutorName = student.user_name?.trim() || student.tutor_name?.trim()
                        const balance = student.balance?.trim()
                        const paymentName = student.payment_name?.trim()
                        const hasVisibleBalance = Boolean(balance) && balance !== '0'
                        const hasVisiblePayment = Boolean(paymentName) && paymentName !== 'Неизвестная платежка'
                        const paymentLogo = hasVisiblePayment ? getPaymentLogo(paymentName) : null

                        return (
                            <SurfaceBlock
                                key={student.id}
                                variant="compact"
                                className={classes.studentCard}
                                onClick={canOpenStudentCard ? () => navigate(`/students/${student.id}`) : undefined}
                            >
                                {
                                    <LabelBadges labels={student.labels} />
                                }
                                {canOpenStudentCard
                                    ? <Link className={classes.nameLink} to={`/students/${student.id}`}>
                                        <span className={classes.studentName}>{student.id}. {studentName}</span>
                                    </Link>
                                    : <span className={classes.studentName}>{student.id}. {studentName}</span>}
                                {tutorName && student.user_id && student.user_id > 0 && (
                                    <span className={classes.studentMeta}>
                                        Исполнитель:{' '}
                                        <Link
                                            className={classes.tgLink}
                                            to={`/staff/${student.user_id}`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {tutorName}
                                        </Link>
                                    </span>
                                )}
                                {
                                    canViewStudentFull
                                        ?
                                    <>
                                        <section className={classes.infoGrid}>
                                            {parentFullName && (
                                                <span className={classes.studentMeta}>
                                                    Родитель: {parentFullName}
                                                </span>
                                            )}
                                            {hasVisibleBalance && (
                                                <span className={classes.studentMeta}>
                                                    Баланс: {balance} ₽
                                                </span>
                                            )}
                                            {hasVisiblePayment && (
                                                <span className={classes.studentMeta}>
                                                    Платежка:
                                                    {
                                                        paymentLogo &&
                                                        <img
                                                            src={paymentLogo.src}
                                                            alt={paymentLogo.alt}
                                                            className={classes.paymentLogo}
                                                        />
                                                    }
                                                    <span className={classes.paymentName}>{paymentName}</span>
                                                </span>
                                            )}
                                            <section className={classes.metaRow}>
                                                {
                                                    student.tg?.trim()
                                                        ?
                                                    <a
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className={classes.tgLink}
                                                        href={student.tg}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        Написать в ТГ
                                                    </a>
                                                        :
                                                    null
                                                }
                                                {
                                                    canReadStudentComments && Boolean(student.comments_count)
                                                        ?
                                                    <section className={classes.commentsWrap}>
                                                        <CommentsCount count={student.comments_count} />
                                                    </section>
                                                        :
                                                    null
                                                }
                                            </section>
                                        </section>
                                    </>
                                        :
                                    <section className={classes.metaRow}>
                                        {
                                            student.tg?.trim()
                                                ?
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                className={classes.tgLink}
                                                href={student.tg}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                Написать в ТГ
                                            </a>
                                                :
                                            null
                                        }
                                    </section>
                                }
                            </SurfaceBlock>
                        )
                    })}
                </section>
            }   
        </section>
    )
}
